import { useState } from 'react';
import {
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { useMyProjects } from '@/api/projects';
import { useAssignCdnProject } from '@/api/manticore/cdn-providers';
import { trackFeatureAction } from '@/lib/analytics-events';

/**
 * Compact dialog to assign (or unassign) a CDN orchestrator umbrella to a project.
 * Mirrors the DomainAssignDialog pattern.
 */
export function UmbrellaAssignProjectDialog({
  open,
  onOpenChange,
  serviceId,
  serviceName,
  currentProjectId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  serviceId: string;
  serviceName: string;
  currentProjectId?: string | null;
}) {
  const { show } = useToast();
  const { data: projects = [] } = useMyProjects();
  // "__unassign__" is a sentinel value for the "Remove from project" option.
  const [selected, setSelected] = useState(currentProjectId ?? '__unassign__');

  const assign = useAssignCdnProject(serviceId);

  const submit = () => {
    const projectId = selected === '__unassign__' ? null : selected;
    assign.mutate(
      { projectId },
      {
        onSuccess: () => {
          trackFeatureAction('cdn', 'provider', 'edit');
          const label = projectId
            ? (projects.find((p) => p.id === projectId)?.name ?? projectId)
            : 'none';
          show({ tone: 'success', title: `Orchestrator assigned to ${label}` });
          onOpenChange(false);
        },
        onError: () => show({ tone: 'danger', title: 'Failed to assign project' }),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign orchestrator to project</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
              Choose a project for <strong>{serviceName}</strong>. The orchestrator will
              appear grouped under that project on the CDN home page.
            </p>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger>
                <SelectValue placeholder="Select a project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__unassign__">— Remove from project</SelectItem>
                {projects.length === 0 ? (
                  <SelectItem value="__none__" disabled>No projects available</SelectItem>
                ) : (
                  projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={assign.isPending} onClick={submit}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
